/**
 * Meta Tag Utilities
 * Helpers for building SEO meta tags in TanStack Router head() exports
 */

import { formatTitle, getSiteUrl, seoConfig } from "./config";

export interface MetaTagsOptions {
	title?: string;
	description?: string;
	canonical?: string;
	ogImage?: string;
	ogType?: "website" | "article" | "profile";
	noIndex?: boolean;
	noFollow?: boolean;
}

/**
 * Build meta tags for a route
 * Returns array compatible with TanStack Router head() meta property
 */
export function buildMetaTags(options: MetaTagsOptions = {}) {
	const {
		title,
		description = seoConfig.siteDescription,
		canonical,
		ogImage = seoConfig.defaultOgImage,
		ogType = "website",
		noIndex = false,
		noFollow = false,
	} = options;

	const formattedTitle = formatTitle(title);
	const fullOgImage = ogImage.startsWith("http") ? ogImage : `${getSiteUrl()}${ogImage}`;

	const robots = buildRobotsDirective({ noIndex, noFollow });

	const meta: Array<{ [key: string]: string }> = [
		{ charSet: "utf-8" },
		{ name: "viewport", content: "width=device-width, initial-scale=1" },
		{ title: formattedTitle },
		{ name: "description", content: description },

		// Open Graph
		{ property: "og:title", content: formattedTitle },
		{ property: "og:description", content: description },
		{ property: "og:type", content: ogType },
		{ property: "og:image", content: fullOgImage },
		{ property: "og:site_name", content: seoConfig.siteName },
		{ property: "og:url", content: canonical || getSiteUrl() },

		// Twitter Cards
		{ name: "twitter:card", content: "summary_large_image" },
		{ name: "twitter:title", content: formattedTitle },
		{ name: "twitter:description", content: description },
		{ name: "twitter:image", content: fullOgImage },
	];

	if (seoConfig.twitterHandle) {
		meta.push({ name: "twitter:site", content: seoConfig.twitterHandle });
	}

	if (robots) {
		meta.push({ name: "robots", content: robots });
	}

	return meta;
}

/**
 * Build links array for head() export
 */
export function buildLinks(options: { canonical?: string } = {}) {
	const links: Array<{ rel: string; href: string }> = [];

	if (options.canonical) {
		links.push({
			rel: "canonical",
			href: options.canonical,
		});
	}

	return links;
}

/**
 * Get canonical URL for a path
 * Normalizes trailing slashes and query params
 */
export function getCanonical(path: string, includeQueryParams = false): string {
	const siteUrl = getSiteUrl();

	// Remove trailing slash unless it's root
	let normalizedPath = path;
	if (normalizedPath !== "/" && normalizedPath.endsWith("/")) {
		normalizedPath = normalizedPath.slice(0, -1);
	}

	// Remove query params unless explicitly included
	if (!includeQueryParams && normalizedPath.includes("?")) {
		normalizedPath = normalizedPath.split("?")[0];
	}

	return `${siteUrl}${normalizedPath}`;
}

/**
 * Build robots directive
 */
function buildRobotsDirective({
	noIndex,
	noFollow,
}: {
	noIndex: boolean;
	noFollow: boolean;
}): string | null {
	if (!noIndex && !noFollow) {
		return seoConfig.defaultRobots;
	}

	const directives: string[] = [];

	directives.push(noIndex ? "noindex" : "index");
	directives.push(noFollow ? "nofollow" : "follow");

	return directives.join(", ");
}
